import { useState } from "react";
import { useLocation } from "react-router-dom";
import type { CustomFieldDto, CustomFieldKind } from "@arcline/shared";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { useAuth } from "../lib/auth";
import { useConfirm } from "./ConfirmDialog";
import { Toast } from "./Toast";
import { c, font } from "../lib/theme";

// The custom-field palette — what "＋ ADD FIELD" offers.
const PALETTE: Array<{ kind: CustomFieldKind; glyph: string; label: string; defLabel: string; options?: string[] }> = [
  { kind: "text", glyph: "T", label: "Text field", defLabel: "New text field" },
  { kind: "enum", glyph: "≣", label: "Enumeration", defLabel: "New enumeration", options: ["Option A", "Option B"] },
  { kind: "date", glyph: "▦", label: "Date", defLabel: "New date" },
  { kind: "doc", glyph: "▤", label: "Document reference", defLabel: "Reference document" },
  { kind: "section", glyph: "§", label: "Section heading", defLabel: "Section" },
];

// The "Edit Layout" dock — pinned to the bottom of every screen. Any signed-in
// user sees the fields attached to the current screen; users with the Edit Model
// capability can add, edit, reorder and remove them. Everything persists
// server-side (custom_fields table) and is audited.
export function FieldsDock() {
  const loc = useLocation();
  const viewKey = loc.pathname;
  const { can } = useAuth();
  const confirm = useConfirm();
  const canEdit = can("editModel");

  const fieldsA = useAsync(() => api.customFields(viewKey), [viewKey]);
  const fields = fieldsA.data ?? [];
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [palette, setPalette] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);

  const reload = fieldsA.reload;
  const fail = (e: unknown): void => setToast((e as Error).message.includes("forbidden") ? "Edit Model capability required" : `Failed: ${(e as Error).message}`);

  const add = async (p: typeof PALETTE[number]): Promise<void> => {
    setPalette(false);
    try { await api.addCustomField({ viewKey, kind: p.kind, label: p.defLabel, options: p.options }); reload(); setEditMode(true); setToast(`Added ${p.label.toLowerCase()}`); }
    catch (e) { fail(e); }
  };
  const patch = async (f: CustomFieldDto, p: Parameters<typeof api.updateCustomField>[1]): Promise<void> => {
    try { await api.updateCustomField(f.id, p); reload(); } catch (e) { fail(e); }
  };
  const remove = async (f: CustomFieldDto): Promise<void> => {
    if (!(await confirm({ title: "Remove field?", message: `Remove "${f.label}" from this screen? This cannot be undone.`, danger: true, confirmLabel: "Remove" }))) return;
    try { await api.deleteCustomField(f.id); reload(); setToast("Field removed"); } catch (e) { fail(e); }
  };
  // Drag-to-reorder: drop src before/at target, then persist the new positions.
  const onDrop = async (targetId: number): Promise<void> => {
    const src = dragId; setDragId(null);
    if (src == null || src === targetId) return;
    const ordered = [...fields].sort((a, b) => a.position - b.position);
    const si = ordered.findIndex((f) => f.id === src), ti = ordered.findIndex((f) => f.id === targetId);
    if (si < 0 || ti < 0) return;
    const [moved] = ordered.splice(si, 1); ordered.splice(ti, 0, moved);
    try {
      await Promise.all(ordered.map((f, i) => (f.position === i ? null : api.updateCustomField(f.id, { position: i }))).filter(Boolean) as Promise<unknown>[]);
      reload();
    } catch (e) { fail(e); }
  };

  const ordered = [...fields].sort((a, b) => a.position - b.position);
  const headBtn = { font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", padding: "5px 11px", borderRadius: 6, cursor: "pointer", border: `1px solid ${c.line2}` } as const;

  return (
    <div style={{ marginTop: 28, borderTop: `1px solid ${c.line}`, paddingTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={() => setOpen((o) => !o)} style={{ ...headBtn, background: c.panel2, color: c.muted2, display: "inline-flex", alignItems: "center", gap: 7 }}>
          <span style={{ color: c.blueHi }}>{open ? "▾" : "▸"}</span>
          <span style={{ letterSpacing: "0.14em" }}>Custom fields &amp; references</span>
          <span style={{ color: c.muted }}>· {fields.length}</span>
        </button>
        <span style={{ font: `500 13px ${font.sans}`, color: c.muted, letterSpacing: "0.06em" }}>{viewKey}</span>
        <div style={{ flex: 1 }} />
        {open && canEdit && (
          <>
            {editMode && (
              <span style={{ position: "relative" }} onMouseLeave={() => setPalette(false)}>
                <button onClick={() => setPalette((p) => !p)} style={{ ...headBtn, background: c.navItem, color: c.blueHi }}>＋ ADD FIELD</button>
                {palette && (
                  <div style={{ position: "absolute", bottom: 34, right: 0, zIndex: 60, width: 210, background: c.soft.tileHi, border: "none", borderRadius: 10, padding: 6, boxShadow: "0 16px 44px rgba(0,0,0,0.5)" }}>
                    {PALETTE.map((p) => (
                      <button key={p.kind} onClick={() => add(p)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "8px 9px", background: "transparent", border: "none", borderRadius: 6, cursor: "pointer", color: c.text }}>
                        <span style={{ width: 18, color: c.blueHi, fontSize: 15, textAlign: "center" }}>{p.glyph}</span>
                        <span style={{ fontSize: 14 }}>{p.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </span>
            )}
            <button onClick={() => { setEditMode((e) => !e); setPalette(false); }} style={{ ...headBtn, background: editMode ? c.blue : c.panel2, color: editMode ? "#fff" : c.muted2, borderColor: editMode ? c.blue : c.line2 }}>
              {editMode ? "◉ EDITING" : "✎ EDIT LAYOUT"}
            </button>
          </>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          {ordered.length === 0 ? (
            <div style={{ font: `400 13px ${font.mono}`, color: c.muted3, lineHeight: 1.6, padding: "4px 2px" }}>
              No custom fields on this screen yet.{canEdit ? " Use ✎ EDIT LAYOUT → ＋ ADD FIELD to attach enumerations, text, dates, or reference documents — they save to this screen and persist." : " A user with the Edit Model capability can add them."}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
              {ordered.map((f) => (
                <FieldCard
                  key={f.id}
                  field={f}
                  editMode={editMode && canEdit}
                  onPatch={patch}
                  onRemove={remove}
                  onDragStart={() => setDragId(f.id)}
                  onDrop={() => onDrop(f.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {toast && <Toast text={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

function FieldCard({ field: f, editMode, onPatch, onRemove, onDragStart, onDrop }: {
  field: CustomFieldDto;
  editMode: boolean;
  onPatch: (f: CustomFieldDto, p: { label?: string; value?: string | null; options?: string[] | null }) => void;
  onRemove: (f: CustomFieldDto) => void;
  onDragStart: () => void;
  onDrop: () => void;
}) {
  const [label, setLabel] = useState(f.label);
  const [value, setValue] = useState(f.value ?? "");
  const [optsText, setOptsText] = useState((f.options ?? []).join(", "));
  const input = { width: "100%", background: c.panel, border: `1px solid ${c.line2}`, borderRadius: 6, color: c.text, fontSize: 14, padding: "7px 9px", boxSizing: "border-box" as const, font: `400 14px ${font.sans}` };
  const labelStyle = { font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.muted, marginBottom: 6 } as const;

  if (f.kind === "section") {
    return (
      <div draggable={editMode} onDragStart={onDragStart} onDragOver={(e) => editMode && e.preventDefault()} onDrop={onDrop}
        style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 10, padding: "8px 2px", borderBottom: `1px solid ${c.line2}` }}>
        {editMode && <span style={{ color: c.muted, cursor: "grab", fontSize: 15 }}>⠿</span>}
        {editMode
          ? <input value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => label !== f.label && onPatch(f, { label })} style={{ ...input, font: `700 14px ${font.display}`, color: c.cyan, flex: 1 }} />
          : <div style={{ font: `700 14px ${font.display}`, letterSpacing: "0.06em", color: c.cyan, flex: 1 }}>{f.label.toUpperCase()}</div>}
        {editMode && <button onClick={() => onRemove(f)} aria-label="Remove" style={{ color: c.redHi, background: "transparent", border: "none", cursor: "pointer", fontSize: 14 }}>✕</button>}
      </div>
    );
  }

  return (
    <div draggable={editMode} onDragStart={onDragStart} onDragOver={(e) => editMode && e.preventDefault()} onDrop={onDrop}
      style={{ background: c.panel2, border: `1px solid ${c.line}`, borderRadius: 8, padding: "11px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        {editMode && <span style={{ color: c.muted, cursor: "grab", fontSize: 14 }}>⠿</span>}
        {editMode
          ? <input value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => label !== f.label && onPatch(f, { label })} style={{ ...input, ...labelStyle, padding: "4px 6px", flex: 1, marginBottom: 0 }} />
          : <div style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>{f.label.toUpperCase()}</div>}
        <span style={{ font: `500 13px ${font.sans}`, color: c.blueHi, letterSpacing: "0.08em" }}>{f.kind.toUpperCase()}</span>
        {editMode && <button onClick={() => onRemove(f)} aria-label="Remove" style={{ color: c.redHi, background: "transparent", border: "none", cursor: "pointer", fontSize: 14 }}>✕</button>}
      </div>

      {editMode && f.kind === "enum" && (
        <input value={optsText} onChange={(e) => setOptsText(e.target.value)} placeholder="comma,separated,options"
          onBlur={() => { const opts = optsText.split(",").map((s) => s.trim()).filter(Boolean); if (opts.join(",") !== (f.options ?? []).join(",")) onPatch(f, { options: opts }); }}
          style={{ ...input, marginBottom: 7, font: `400 13px ${font.mono}` }} />
      )}

      {f.kind === "enum" ? (
        <select value={value} disabled={!editMode} onChange={(e) => { setValue(e.target.value); onPatch(f, { value: e.target.value || null }); }} style={{ ...input, opacity: editMode ? 1 : 0.85 }}>
          <option value="">—</option>
          {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : f.kind === "date" ? (
        <input type="date" value={value} disabled={!editMode} onChange={(e) => setValue(e.target.value)} onBlur={() => (value || null) !== f.value && onPatch(f, { value: value || null })} style={{ ...input, opacity: editMode ? 1 : 0.85 }} />
      ) : (
        <input value={value} disabled={!editMode} placeholder={editMode ? (f.kind === "doc" ? "Document name or link…" : "Value…") : "—"}
          onChange={(e) => setValue(e.target.value)} onBlur={() => (value || null) !== f.value && onPatch(f, { value: value || null })}
          style={{ ...input, opacity: editMode ? 1 : 0.85 }} />
      )}
    </div>
  );
}
